import React, { FormEventHandler, useState } from "react"
import moment, { Moment } from "moment"
import { useTranslation } from "react-i18next"
import { Box, Dialog, DialogContent, TextField, Typography } from "@mui/material"
import { ReactComponent as IdCard } from "../../icon/age-gate-id-card.svg"
import BobbingIcon from "../common/BobbingIcon"
import BlueButton from "../buttons/BlueButton"

interface AgeGateProps {
  show: boolean
  prompt: string
  minimumAge: number
  passAgeGate: (didPass: boolean) => void
}

function AgeGateModal({ show, prompt, minimumAge, passAgeGate }: AgeGateProps) {
  const { t } = useTranslation()

  const [birthDate, setBirthDate] = useState<Moment | null>(null)
  const [isFormValid, setIsFormValid] = useState<boolean | null>(null)

  const validate = (date: string) => {
    let parsedDate = moment(date)

    if (parsedDate.isBefore(moment().subtract(500, "years"))) {
      return
    }

    setIsFormValid(
      parsedDate.isAfter(moment().subtract(115, "years")) && parsedDate.isBefore(moment()),
    )
    setBirthDate(parsedDate)
  }

  const submit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault()

    if (!birthDate) {
      return
    }

    let now = moment()
    let dateMarker = now.subtract(minimumAge, "years")

    passAgeGate(birthDate.isBefore(dateMarker))
  }

  return (
    <Dialog
      open={show}
      disableEscapeKeyDown
      slotProps={{ paper: { sx: { borderRadius: "0.7rem", color: "#323232", maxWidth: 480 } } }}
    >
      <DialogContent sx={{ p: { xs: 3, sm: 4 }, textAlign: "center" }}>
        <BobbingIcon
          horizontalOffset="-120px"
          verticalOffset="-90px"
        >
          <IdCard />
        </BobbingIcon>

        <Typography fontSize={28} fontWeight={500}>{prompt}</Typography>
        <Typography fontSize={18} fontWeight={400} sx={{ mt: 1 }}>{t("ageGate.text")}</Typography>

        <Box
          component="form"
          onSubmit={submit}
          sx={{ display: "flex", mt: 2, gap: 0 }}
        >
          <TextField
            type="date"
            name="birthDate"
            aria-label="Birth date"
            inputProps={{ max: moment().format("YYYY-MM-DD") }}
            error={isFormValid === false}
            onChange={(e) => validate(e.target.value)}
            required
            size="small"
            sx={{
              flex: 1,
              "& .MuiInputBase-root": { height: 50, borderRadius: "4px 0 0 4px" },
              "& input": { pl: "20px" },
            }}
          />
          <BlueButton
            type="submit"
            disabled={!isFormValid}
            sx={{
              height: 50,
              minWidth: { xs: 90, sm: 120 },
              borderRadius: "0 4px 4px 0",
              px: 0,
            }}
          >
            {t("ageGate.submit")}
          </BlueButton>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default AgeGateModal
